/**
 * Estado do formulario "Novo envio em massa" e suas regras puras.
 *
 * Fica fora do componente para que "quantos contatos" e "o que falta" sejam
 * funcoes usadas pelas DUAS colunas (formulario e previa) sem duplicar a
 * conta. Os botoes "Agendar" e "Iniciar envio" so habilitam quando
 * `pendencias` esta vazia -- e a lista e mostrada por extenso, para a pessoa
 * saber o que falta em vez de encarar um botao cinza.
 *
 * Decisoes nao obvias:
 *  1. `vendedoras` vazio = RODIZIO entre todas as vendedoras de papel
 *     comercial. O numero de saida e o do comercial da empresa (E5): quem
 *     responde cai com elas e a venda que elas fecharem continua creditada
 *     ao toque.
 *  2. `pularRecentes` vem LIGADO: quem recebeu envio em massa nos ultimos 7
 *     dias sai do publico. Duas mensagens na mesma semana e o caminho mais
 *     curto para o SAIR e para o bloqueio do numero.
 *  3. `pausarSeOptOutAlto` vem LIGADO: acima de 3% de opt-out o envio para
 *     sozinho e a equipe decide se retoma. Protege o numero do comercial,
 *     que e o ativo da empresa, nao nosso.
 */

import { SEGMENT_SHORT_LABEL } from './vocabulario';
import type { AudienceSource, Segment, SegmentKey } from './types';

export interface CsvResumo {
  readonly fileName: string;
  readonly total: number;
  readonly invalidos: number;
}

/** Limite da trava automatica de opt-out: 3,00%. */
export const LIMITE_AUTOPAUSA_BPS = 300;

export const RITMO_MINIMO = 5;
export const RITMO_MAXIMO = 120;

export interface FormularioEnvio {
  readonly templateId: string | null;
  readonly fonte: AudienceSource;
  readonly segmentos: readonly SegmentKey[];
  readonly csv: CsvResumo | null;
  readonly pularRecentes: boolean;
  readonly nome: string;
  /** Ids das vendedoras que recebem as respostas; vazio = rodizio. */
  readonly vendedoras: readonly string[];
  readonly avancadoAberto: boolean;
  /** Mensagens por minuto. Ritmo baixo protege o numero de bloqueio. */
  readonly porMinuto: number;
  /** HH:MM, no fuso da empresa. */
  readonly janelaInicio: string;
  readonly janelaFim: string;
  readonly pausarSeOptOutAlto: boolean;
  readonly consentimento: boolean;
}

export function formularioInicial(segmentoInicial: SegmentKey | null): FormularioEnvio {
  return {
    templateId: null,
    fonte: 'segmentos',
    segmentos: segmentoInicial === null ? [] : [segmentoInicial],
    csv: null,
    pularRecentes: true,
    nome: '',
    vendedoras: [],
    avancadoAberto: false,
    porMinuto: 20,
    janelaInicio: '09:00',
    janelaFim: '20:00',
    pausarSeOptOutAlto: true,
    consentimento: false,
  };
}

export interface PublicoCalculado {
  /** Soma dos segmentos (teto: um contato pode estar em dois). */
  readonly bruto: number;
  /** Removidos por terem recebido envio nos ultimos 7 dias. */
  readonly recentes: number;
  readonly contatos: number;
}

/**
 * Contatos do publico escolhido. Segmentos podem se sobrepor, entao a soma e
 * um teto -- a tela diz isso. CSV conta so as linhas validas.
 */
export function publicoDoFormulario(form: FormularioEnvio, segments: readonly Segment[]): PublicoCalculado {
  if (form.fonte === 'csv') {
    const validos = form.csv === null ? 0 : Math.max(0, form.csv.total - form.csv.invalidos);
    return { bruto: validos, recentes: 0, contatos: validos };
  }
  const escolhidos = segments.filter((s) => form.segmentos.includes(s.key));
  const bruto = escolhidos.reduce((acc, s) => acc + s.estimatedCount, 0);
  const recentes = form.pularRecentes ? escolhidos.reduce((acc, s) => acc + s.recentlyContactedCount, 0) : 0;
  return { bruto, recentes, contatos: Math.max(0, bruto - recentes) };
}

/** Minutos entre dois "HH:MM"; zero quando o fim nao vem depois do inicio. */
export function minutosDaJanela(inicio: string, fim: string): number {
  const paraMinutos = (hhmm: string): number | null => {
    const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
    if (m === null) {
      return null;
    }
    return Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0);
  };
  const a = paraMinutos(inicio);
  const b = paraMinutos(fim);
  if (a === null || b === null || b <= a) {
    return 0;
  }
  return b - a;
}

export function pendenciasDoFormulario(form: FormularioEnvio, contatos: number): readonly string[] {
  const pendencias: string[] = [];
  if (form.templateId === null) {
    pendencias.push('escolha um modelo de mensagem');
  }
  if (contatos <= 0) {
    pendencias.push(form.fonte === 'csv' ? 'importe um CSV com contatos válidos' : 'marque ao menos um público');
  }
  if (form.nome.trim() === '') {
    pendencias.push('dê um nome ao envio');
  }
  if (minutosDaJanela(form.janelaInicio, form.janelaFim) <= 0) {
    pendencias.push('ajuste a janela de horário (o fim precisa vir depois do início)');
  }
  if (!form.consentimento) {
    pendencias.push('confirme as políticas de disparo');
  }
  return pendencias;
}

export function alternarItem<T extends string>(lista: readonly T[], item: T): readonly T[] {
  return lista.includes(item) ? lista.filter((k) => k !== item) : [...lista, item];
}

/** "Responderam sem fechar · 24/09": nome pronto a partir do publico e do dia. */
export function sugerirNome(form: FormularioEnvio, hoje: Date): string {
  const dia = `${String(hoje.getDate()).padStart(2, '0')}/${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  if (form.fonte === 'csv') {
    return `Lista importada · ${dia}`;
  }
  const rotulos = form.segmentos.map((k) => SEGMENT_SHORT_LABEL[k]);
  if (rotulos.length === 0) {
    return `Envio · ${dia}`;
  }
  const texto = rotulos.length > 2 ? `${rotulos.slice(0, 2).join(' + ')} +${rotulos.length - 2}` : rotulos.join(' + ');
  return `${texto} · ${dia}`;
}
